import React from 'react'
import EmptyState from '../../../../components/EmptyScreen'
import RestaurantCard from '../../../../components/restaurant/RestaurantCard'
import { ROUTES } from '../../../../helpers/routes-helper'
import { DEFAULT_CARD_IMAGE } from '../../../../helpers/static-constants'
import useAppUtilities from '../../../../hooks/useAppUtilities'
import { useAppSelector } from '../../../../store'
import {
  selectMessage,
  selectSeverity,
  selectShouldOpen,
  setOpen,
} from '../../../../store/restaurantSlice'
import { selectSearchId } from '../../../../store/searchSlice'
import { logEvent } from '../../../../analytics/analytics'
import ImpressionTracker from '../../../../components/analytics/ImpressionTracker'
import { RestaurantAndPredictedRestaurant } from '../../../../types'
import { MuiAlert, MuiSnackbar } from '../../styled/global.styled'
import {
  SearchResultRestaurantsInnerGrid,
  SearchResultRestaurantsMainGrid,
  SearchResultRestaurantsWrapper,
} from '../styled/SearchResultRestaurants.styled'

interface SearchResultRestaurantsProps {
  restaurantList: RestaurantAndPredictedRestaurant[]
  term?: string
}

const SearchResultRestaurants: React.FC<SearchResultRestaurantsProps> = ({
  restaurantList,
  term,
}) => {
  const { navigate, dispatch } = useAppUtilities()
  const message = useAppSelector(selectMessage)
  const severity = useAppSelector(selectSeverity)
  const openServer = useAppSelector(selectShouldOpen)
  const searchId = useAppSelector(selectSearchId)

  const setOpenServer = (): void => {
    dispatch(setOpen(false))
  }
  return (
    <>
      <SearchResultRestaurantsMainGrid>
        {restaurantList.length > 0 &&
          restaurantList.map((card, index) => {
            return (
              <SearchResultRestaurantsInnerGrid key={index}>
                <SearchResultRestaurantsWrapper
                  key={index}
                  onClick={() => {
                    // Guard: only emit the query->click pair when we actually have the selected
                    // item's canonical id and a session search_id. Prevents the empty/malformed
                    // payloads that made every search_result_selected event `_invalid` (BUG 3).
                    const restaurantId = card?.restaurantDTO?.restaurantId
                    if (restaurantId && searchId) {
                      logEvent('search_result_selected', {
                        search_id: searchId,
                        restaurant_id: restaurantId,
                        rank_position: index,
                        term,
                        screen_name: 'search_results',
                      })
                    }
                    navigate(
                      `${ROUTES.RESTAURANT_DETAILS}/${card?.restaurantDTO?.alias}/details`,
                    )
                  }}
                >
                  <ImpressionTracker
                    onImpress={() => {
                      logEvent('restaurant_impression', {
                        restaurant_id: card?.restaurantDTO?.restaurantId ?? '',
                        rank_position: index,
                        search_id: searchId ?? undefined,
                        screen_name: 'search_results',
                      })
                    }}
                  >
                    <RestaurantCard
                      id={card.restaurantDTO.restaurantId}
                      alias={card?.restaurantDTO?.alias ?? ''}
                      imgList={[
                        card?.restaurantDTO?.imageUrl ??
                        card?.restaurantDTO?.imageUrl !== ''
                          ? card?.restaurantDTO?.imageUrl ?? DEFAULT_CARD_IMAGE
                          : DEFAULT_CARD_IMAGE,
                      ]}
                      title={card?.restaurantDTO?.name}
                      isVerified={card?.restaurantDTO?.verified ?? false}
                      subTitle={[
                        card?.restaurantDTO?.address1,
                        card?.restaurantDTO?.city,
                        card?.restaurantDTO?.state,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                      distance={`${card?.restaurantDTO?.distance ?? ''}`}
                      searchId={searchId ?? undefined}
                      phone={card?.restaurantDTO?.phone}
                      latitude={card?.restaurantDTO?.lat}
                      longitude={card?.restaurantDTO?.lng}
                      placeId={card?.restaurantDTO?.placeId}
                      address={[
                        card?.restaurantDTO?.address1,
                        card?.restaurantDTO?.city,
                        card?.restaurantDTO?.state,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                      reservationUrl={card?.restaurantDTO?.reservationUrl}
                      website={card?.restaurantDTO?.website}
                      cuisine={card?.restaurantDTO?.cuisine ?? ''}
                      city={card?.restaurantDTO?.city ?? ''}
                      rankPosition={index}
                      plannerSourceSurface="search"
                      tagChipData={[
                        {
                          percentage: `${card.value?.toLocaleString() ?? ''}`,
                          title: 'Match',
                        },
                      ]}
                    />
                  </ImpressionTracker>
                </SearchResultRestaurantsWrapper>
              </SearchResultRestaurantsInnerGrid>
            )
          })}
        {restaurantList.length === 0 && (
          <EmptyState
            containerHeight={'600px'}
            mainText="Oops! No Results Found"
          />
        )}
      </SearchResultRestaurantsMainGrid>
      <MuiSnackbar open={openServer} onClose={setOpenServer}>
        <MuiAlert onClose={setOpenServer} severity={severity ?? 'success'}>
          {message}
        </MuiAlert>
      </MuiSnackbar>
    </>
  )
}
export default SearchResultRestaurants
